import { useMutation } from '@tanstack/react-query';
import type { JSX } from 'react';

import { ConfirmDialog } from '@/components/ConfirmDialog';

import {
  ADMIN_REASON_LIMITS,
  resetPlatformAdminMfa,
  setPlatformAdminActive,
  unlockPlatformAdmin,
  type PlatformAdmin,
} from '../api/platform-admins.graphql';

export type AdminAction = 'deactivate' | 'reactivate' | 'reset-mfa' | 'unlock';

const ACTIONS: Record<
  AdminAction,
  { title: string; confirm: string; danger: boolean; text: string; placeholder: string }
> = {
  deactivate: {
    title: 'Desactivar',
    confirm: 'Desactivar',
    danger: true,
    text: 'Pierde el acceso de inmediato, incluso con una sesión abierta. Su historial se conserva y puedes reactivarlo; al hacerlo deberá iniciar sesión de nuevo.',
    placeholder: 'Ej.: dejó la empresa',
  },
  reactivate: {
    title: 'Reactivar',
    confirm: 'Reactivar',
    danger: false,
    text: 'Vuelve a poder iniciar sesión con su contraseña y su MFA actuales.',
    placeholder: 'Ej.: regresa al equipo de soporte',
  },
  'reset-mfa': {
    title: 'Reiniciar MFA de',
    confirm: 'Reiniciar MFA',
    danger: true,
    text: 'Se borran su autenticador y sus códigos de recuperación y se cierran de inmediato todas sus sesiones abiertas. En su próximo inicio de sesión deberá configurar el MFA de nuevo.',
    placeholder: 'Ej.: perdió el celular con el autenticador',
  },
  unlock: {
    title: 'Desbloquear',
    confirm: 'Desbloquear',
    danger: false,
    text: 'Se borran los intentos fallidos y el bloqueo temporal: puede volver a intentar el inicio de sesión ya.',
    placeholder: 'Ej.: confirmado por teléfono que fue él quien se equivocó',
  },
};

function run(action: AdminAction, adminId: string, reason: string): Promise<PlatformAdmin> {
  switch (action) {
    case 'deactivate':
      return setPlatformAdminActive(adminId, false, reason);
    case 'reactivate':
      return setPlatformAdminActive(adminId, true, reason);
    case 'reset-mfa':
      return resetPlatformAdminMfa(adminId, reason);
    case 'unlock':
      return unlockPlatformAdmin(adminId, reason);
  }
}

interface Props {
  action: AdminAction;
  admin: PlatformAdmin;
  onDone: (admin: PlatformAdmin) => void;
  onCancel: () => void;
}

/** OS-1717 — acciones sobre un administrador: todas con motivo, auditadas por el API. */
export function AdminActionDialog({ action, admin, onDone, onCancel }: Props): JSX.Element {
  const copy = ACTIONS[action];
  const mutation = useMutation({
    mutationFn: (reason: string) => run(action, admin.id, reason),
    onSuccess: onDone,
  });

  return (
    <ConfirmDialog
      title={`${copy.title} ${admin.email}`}
      confirmLabel={copy.confirm}
      danger={copy.danger}
      pending={mutation.isPending}
      error={mutation.error?.message ?? null}
      reason={{ label: 'Motivo (queda en la auditoría)', placeholder: copy.placeholder, ...ADMIN_REASON_LIMITS }}
      onConfirm={(reason) => mutation.mutate(reason)}
      onCancel={onCancel}
    >
      <p>{copy.text}</p>
    </ConfirmDialog>
  );
}
